import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { 
  AlertCircle, 
  Loader2, 
  Send, 
  Clock, 
  User, 
  Tag, 
  CheckCircle,
  XCircle,
  FileText,
  Building2,
  Mail,
  ExternalLink,
  Calendar,
  Users,
  MapPin,
  Gavel
} from 'lucide-react';
import { NoteFeedbackApiService, PublicNoteData } from '../services/notesV2Api';
import toast from 'react-hot-toast';
import Button from '../components/Button';
import DistrictMapLink from '../components/DistrictMapLink';
import { formatDate } from '../utils/dateUtils';
import { formatDistrict, padDistrictNumber } from '../utils/districtDisplay';

// Apply dark mode immediately on page load to prevent flash
// This runs before React hydrates and respects system preference
if (typeof window !== 'undefined') {
  try {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;

    if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
      document.documentElement.classList.add('dark');
    } else if (savedTheme === 'light') {
      document.documentElement.classList.remove('dark');
    } else {
      document.documentElement.classList.add('dark');
    }
  } catch {
    document.documentElement.classList.add('dark');
  }
}

type PageState = 'email_verification' | 'viewing' | 'submitted' | 'error';

interface ErrorState {
  code: string;
  message: string;
}

// Helper to format date
const formatEntityDate = (dateString: string) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

// Helper to get party color
const getPartyColor = (party: string) => {
  if (!party) return 'text-text-secondary dark:text-text-dark-secondary';
  const p = party.toLowerCase();
  if (p.includes('democrat')) return 'text-blue-600 dark:text-blue-400';
  if (p.includes('republican')) return 'text-red-600 dark:text-red-400';
  return 'text-text-secondary dark:text-text-dark-secondary';
};

// Helper to generate internal bill URL
const getBillUrl = (billNumber: string) => {
  if (!billNumber) return null;
  return `/public/bills/${encodeURIComponent(billNumber)}`;
};

// Component to display entity details
const EntityDetailCard: React.FC<{ entity: any }> = ({ entity }) => {
  const details = entity.details;
  
  // Bill entity
  if (details?.type === 'bill') {
    return (
      <div className="p-4 rounded-lg border border-border-light dark:border-border-dark bg-background-tertiary dark:bg-background-dark-tertiary">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Gavel className="w-4 h-4 text-primary-600 dark:text-primary-400" />
              <span className="font-semibold text-primary-600 dark:text-primary-400">
                {details.bill_number}
              </span>
              {details.status && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-background-tertiary dark:bg-background-dark-secondary text-text-secondary dark:text-text-dark-secondary">
                  {details.status}
                </span>
              )}
            </div>
            
            <h4 className="font-medium text-text-primary dark:text-text-dark-primary mb-2">
              {details.title || details.concerning || 'Untitled Bill'}
            </h4>
            
            {details.concerning && details.title && details.concerning !== details.title && (
              <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-2">
                {details.concerning}
              </p>
            )}
            
            {/* Sponsors */}
            {details.prime_sponsors && details.prime_sponsors.length > 0 && (
              <div className="flex items-start gap-2 text-sm mb-2">
                <Users className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary mt-0.5" />
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  {details.prime_sponsors.map((sponsor: any, idx: number) => (
                    <span key={idx} className={getPartyColor(sponsor.party)}>
                      {sponsor.name}
                      {sponsor.party && ` (${sponsor.party.charAt(0)})`}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Committees */}
            {(details.house_committee || details.senate_committee) && (
              <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                {details.house_committee && <span>House: {details.house_committee}</span>}
                {details.house_committee && details.senate_committee && <span className="mx-2">•</span>}
                {details.senate_committee && <span>Senate: {details.senate_committee}</span>}
              </div>
            )}
          </div>
          
          {/* Bill detail link */}
          {details.bill_number && (
            <a
              href={getBillUrl(details.bill_number) || '#'}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors"
            >
              <span>View Bill Details</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    );
  }
  
  // Legislator entity
  if (details?.type === 'legislator') {
    return (
      <div className="p-4 rounded-lg border border-border-light dark:border-border-dark bg-background-tertiary dark:bg-background-dark-tertiary">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {details.photo_url ? (
              <img 
                src={details.photo_url} 
                alt={details.name}
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-background-tertiary dark:bg-background-dark-secondary flex items-center justify-center">
                <User className="w-6 h-6 text-text-tertiary dark:text-text-dark-tertiary" />
              </div>
            )}
            <div>
              <h4 className={`font-medium ${getPartyColor(details.party)}`}>
                {details.name}
              </h4>
              <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                {details.party && `${details.party} • `}
                {details.chamber && `${details.chamber}`}
                {details.district && details.chamber ? (
                  <>
                    {' '}
                    <DistrictMapLink
                      chamber={details.chamber}
                      district={details.district}
                      className="text-sm text-text-secondary dark:text-text-dark-secondary"
                    >
                      {formatDistrict(null, String(details.district ?? ''), details.chamber)}
                    </DistrictMapLink>
                  </>
                ) : details.district ? (
                  ` District ${padDistrictNumber(details.district)}`
                ) : null}
              </p>
            </div>
          </div>
          
          {/* External link */}
          {details.external_url && (
            <a
              href={details.external_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors"
            >
              <span>View Profile</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    );
  }
  
  // Calendar event entity
  if (details?.type === 'calendar') {
    return (
      <div className="p-4 rounded-lg border border-border-light dark:border-border-dark bg-background-tertiary dark:bg-background-dark-tertiary">
        <div className="flex items-start gap-3">
          <Calendar className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-0.5" />
          <div>
            <h4 className="font-medium text-text-primary dark:text-text-dark-primary">
              {details.title || 'Calendar Event'}
            </h4>
            {details.event_date && (
              <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                {formatEntityDate(details.event_date)}
                {details.event_time && ` at ${details.event_time}`}
              </p>
            )}
            {details.location && (
              <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {details.location}
              </p>
            )}
            {details.description && (
              <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-2">
                {details.description}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  // Fallback for unknown entity types or entities without details
  return (
    <div className="p-3 rounded-lg border border-border-light dark:border-border-dark bg-background-tertiary dark:bg-background-dark-tertiary">
      <div className="flex items-center gap-2">
        <Tag className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />
        <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
          {entity.entity_display_name || `${entity.entity_type_display}: ${entity.entity_id}`}
        </span>
      </div>
    </div>
  );
};

const NoteFeedbackPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  
  const [pageState, setPageState] = useState<PageState>('email_verification');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [responseContent, setResponseContent] = useState('');
  const [noteData, setNoteData] = useState<PublicNoteData | null>(null);
  const [error, setError] = useState<ErrorState | null>(null);

  const handleEmailVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !token) {
      toast.error('Please enter your email address');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const data = await NoteFeedbackApiService.getShareInfo(token, email);
      setNoteData(data);
      
      // Pre-fill existing response if any
      if (data.existing_response) {
        setResponseContent(data.existing_response.content);
      }
      
      setPageState('viewing');
      
    } catch (err: any) {
      const errorCode = err.response?.data?.error_code;
      const errorMessage = err.response?.data?.error || 'An error occurred';
      
      setError({
        code: errorCode || 'UNKNOWN',
        message: errorMessage
      });
      setPageState('error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitFeedback = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!responseContent.trim() || !token) {
      toast.error('Please enter your response');
      return;
    }
    
    // Name is required for new responses
    if (!noteData?.existing_response && !displayName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    try {
      setSubmitting(true);
      
      await NoteFeedbackApiService.submitFeedback(
        token, 
        email, 
        responseContent,
        noteData?.existing_response ? undefined : displayName.trim()
      );
      
      setPageState('submitted');
      toast.success('Thank you for your feedback!');
      
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to submit feedback';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };


  const getErrorIcon = (code: string) => {
    switch (code) {
      case 'EXPIRED':
        return <Clock className="w-16 h-16 text-warning-500" />;
      case 'REVOKED':
        return <XCircle className="w-16 h-16 text-error-500" />;
      case 'EMAIL_MISMATCH':
        return <Mail className="w-16 h-16 text-error-500" />;
      default:
        return <AlertCircle className="w-16 h-16 text-error-500" />;
    }
  };

  const renderHeader = () => (
    <header className="bg-white dark:bg-background-dark-secondary border-b border-border-light dark:border-border-dark">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center gap-4">
          {noteData?.organization.logo_url ? (
            <img 
              src={noteData.organization.logo_url} 
              alt={noteData.organization.name || 'Organization'} 
              className="h-12 w-auto object-contain"
            />
          ) : (
            <Building2 className="w-10 h-10 text-primary-600 dark:text-primary-400" />
          )}
          <div>
            <h1 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary">
              {noteData?.organization.name || 'Feedback Request'}
            </h1>
            {noteData?.shared_by && (
              <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                Shared by {noteData.shared_by}
              </p>
            )}
          </div>
        </div>
      </div>
    </header>
  );

  return (
    <div className="min-h-screen bg-background-secondary dark:bg-background-dark">
        {/* Header - only show when we have data */}
        {noteData && renderHeader()}
        
        <main className="max-w-3xl mx-auto px-4 py-8">
          {/* Email Verification State */}
          {pageState === 'email_verification' && (
            <div className="card p-8 text-center">
              <FileText className="w-16 h-16 mx-auto text-primary-600 dark:text-primary-400 mb-6" />
              <h2 className="text-2xl font-semibold text-text-primary dark:text-text-dark-primary mb-2">
                Feedback Request
              </h2>
              <p className="text-text-secondary dark:text-text-dark-secondary mb-8">
                Enter your email address to view the note and provide feedback.
              </p>
              
              <form onSubmit={handleEmailVerification} className="max-w-sm mx-auto">
                <div className="mb-4">
                  <label 
                    htmlFor="email" 
                    className="block text-left text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2"
                  >
                    Email Address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                    autoFocus
                  />
                </div>
                
                <Button
                  type="submit"
                  variant="primary"
                  className="w-full"
                  disabled={loading || !email}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Continue'
                  )}
                </Button>
              </form>
            </div>
          )}

          {/* Error State */}
          {pageState === 'error' && error && (
            <div className="card p-8 text-center">
              {getErrorIcon(error.code)}
              <h2 className="text-2xl font-semibold text-text-primary dark:text-text-dark-primary mt-6 mb-2">
                {error.code === 'EXPIRED' ? 'Link Expired' :
                 error.code === 'REVOKED' ? 'Link Revoked' :
                 error.code === 'EMAIL_MISMATCH' ? 'Email Not Recognized' :
                 'Unable to Access'}
              </h2>
              <p className="text-text-secondary dark:text-text-dark-secondary mb-6">
                {error.message}
              </p>
              
              {error.code === 'EMAIL_MISMATCH' && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setError(null);
                    setPageState('email_verification');
                  }}
                >
                  Try a Different Email
                </Button>
              )}
            </div>
          )}

          {/* Viewing State - Show Note and Response Form */}
          {pageState === 'viewing' && noteData && (
            <div className="space-y-6">
              {/* Note Content Card */}
              <div className="card p-6">
                <div className="flex items-start gap-3 mb-4">
                  <FileText className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-1 flex-shrink-0" />
                  <div className="flex-1">
                    {noteData.note.title && (
                      <h2 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary mb-2">
                        {noteData.note.title}
                      </h2>
                    )}
                    <div className="prose prose-sm dark:prose-invert max-w-none text-text-secondary dark:text-text-dark-secondary">
                      <div dangerouslySetInnerHTML={{ __html: noteData.note.content }} />
                    </div>
                  </div>
                </div>

                {/* Note Metadata */}
                <div className="flex flex-wrap items-center gap-4 text-xs text-text-tertiary dark:text-text-dark-tertiary mt-4 pt-4 border-t border-border-light dark:border-border-dark">
                  <div className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    <span>{noteData.note.author_name}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    <span>{formatDate(noteData.note.created_at)}</span>
                  </div>
                </div>

                {/* Entity Context */}
                {noteData.note.entities && noteData.note.entities.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-border-light dark:border-border-dark">
                    <p className="text-xs font-medium text-text-tertiary dark:text-text-dark-tertiary mb-3">
                      Related To:
                    </p>
                    <div className="space-y-4">
                      {noteData.note.entities.map((entity: any, index: number) => (
                        <EntityDetailCard key={index} entity={entity} />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Response Form Card */}
              <div className="card p-6">
                <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-4">
                  {noteData.existing_response ? 'Update Your Response' : 'Your Response'}
                </h3>
                
                {noteData.existing_response && (
                  <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-4">
                    You previously responded on {formatDate(noteData.existing_response.submitted_at)}.
                    You can update your response below.
                  </p>
                )}
                
                <form onSubmit={handleSubmitFeedback}>
                  {/* Name field - only show if not already responded */}
                  {!noteData.existing_response && (
                    <div className="mb-4">
                      <label 
                        htmlFor="displayName" 
                        className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2"
                      >
                        Your Name <span className="text-text-tertiary dark:text-text-dark-tertiary">(how you&apos;ll appear on this note)</span>
                      </label>
                      <input
                        id="displayName"
                        type="text"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        placeholder="Enter your name"
                        className="w-full px-4 py-3 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        required
                        maxLength={255}
                      />
                    </div>
                  )}
                  
                  <div className="mb-4">
                    <label 
                      htmlFor="responseContent" 
                      className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2"
                    >
                      Your Feedback
                    </label>
                    <textarea
                      id="responseContent"
                      value={responseContent}
                      onChange={(e) => setResponseContent(e.target.value)}
                      placeholder="Enter your feedback or comments..."
                      rows={6}
                      className="w-full px-4 py-3 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                      required
                      maxLength={10000}
                    />
                    <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1 text-right">
                      {responseContent.length} / 10,000 characters
                    </p>
                  </div>
                  
                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={submitting || !responseContent.trim()}
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          {noteData.existing_response ? 'Update Response' : 'Submit Response'}
                        </>
                      )}
                    </Button>
                  </div>
                </form>
                
                {/* Expiration Notice */}
                <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-4 text-center">
                  This link expires on {formatDate(noteData.expires_at)}
                </p>
              </div>
            </div>
          )}

          {/* Submitted State */}
          {pageState === 'submitted' && (
            <div className="card p-8 text-center">
              <CheckCircle className="w-16 h-16 mx-auto text-success-500 mb-6" />
              <h2 className="text-2xl font-semibold text-text-primary dark:text-text-dark-primary mb-2">
                Thank You!
              </h2>
              <p className="text-text-secondary dark:text-text-dark-secondary mb-6">
                Your feedback has been submitted successfully.
              </p>
              <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
                You can close this page now. If you need to update your response,
                you can use the same link to access this page again.
              </p>
            </div>
          )}
        </main>
        
        {/* Footer */}
        <footer className="text-center py-6 text-xs text-text-tertiary dark:text-text-dark-tertiary">
          <p>Powered by Capitol Commons</p>
        </footer>
      </div>
  );
};

export default NoteFeedbackPage;

